import React from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Award, CheckCircle, TrendingUp, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';
import { getFullUrl } from '../utils/url';

const About = () => {
  return (
    <div className="min-h-screen">
      <SEO 
        title="À Propos - MMD CONSEIL | Notre Équipe d'Experts-Comptables"
        description="Découvrez MMD CONSEIL : Makhan DIALLO, expert-comptable diplômé DEC et fondateur du cabinet. Expertise comptable, fiscalité et conseil aux PME à Lyon."
        keywords="MMD CONSEIL équipe, Makhan DIALLO expert-comptable, cabinet expertise comptable conseil Lyon"
        url={getFullUrl("/a-propos")}
      />
      
      {/* Hero Section */}
      <section className="hero-gradient pt-20 pb-16 md:pt-28 md:pb-24">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <div className="fade-in-up">
              <Badge className="mb-6 bg-white text-primary-blue px-4 py-2 text-sm font-medium">
                À Propos
              </Badge>
              <h1 className="text-4xl md:text-6xl font-bold text-white mb-6 leading-tight">
                Notre Équipe
              </h1>
              <p className="text-xl md:text-2xl text-white leading-relaxed max-w-3xl mx-auto">
                Une expertise solide pour vous accompagner dans tous vos projets comptables et fiscaux
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Notre Équipe */}
      <section className="section-spacing bg-gradient-to-br from-light-blue to-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <Badge className="mb-6 bg-primary-blue text-white px-4 py-2 text-sm font-medium">
              Notre Équipe
            </Badge>
            <h2 className="text-3xl md:text-4xl font-bold text-text-dark mb-4">
              L'Équipe de MMD CONSEIL
            </h2>
            <p className="text-xl text-text-gray max-w-2xl mx-auto">
              Une expertise au service de votre réussite
            </p>
          </div>

          <div className="max-w-2xl mx-auto">
            {/* Makhan DIALLO */}
            <Card className="bg-white shadow-xl hover:shadow-2xl transition-all duration-300">
              <CardContent className="p-8">
                <div className="text-center mb-6">
                  <div className="w-40 h-40 mx-auto mb-4 rounded-full overflow-hidden border-4 border-primary-blue">
                    <img 
                      src="/makhan-diallo.jpg" 
                      alt="Makhan DIALLO" 
                      className="w-full h-full object-cover object-top"
                    />
                  </div>
                  <h3 className="text-2xl font-bold text-primary-blue mb-2">
                    Makhan DIALLO
                  </h3>
                  <p className="text-accent-blue font-medium mb-1">Fondateur & Expert-Comptable</p>
                  <div className="flex items-center justify-center space-x-2 text-text-gray text-sm">
                    <Award className="w-4 h-4" />
                    <span>Diplômé d'Expertise Comptable (DEC)</span>
                  </div>
                </div>

                <div className="space-y-4 text-text-gray">
                  <p className="leading-relaxed">
                    <strong className="text-text-dark">Fondateur et Expert-Comptable diplômé DEC</strong>, Makhan DIALLO a fondé MMD CONSEIL pour offrir un accompagnement de qualité aux entrepreneurs et PME.
                  </p>

                  <div className="bg-light-blue p-4 rounded-lg">
                    <h4 className="font-semibold text-primary-blue mb-3 flex items-center">
                      <Award className="w-5 h-5 mr-2" />
                      Parcours Professionnel
                    </h4>
                    <ul className="space-y-2 text-sm">
                      <li className="flex items-start">
                        <CheckCircle className="w-4 h-4 text-primary-blue mr-2 mt-0.5 flex-shrink-0" />
                        <span><strong>Plus de 10 ans d'expérience</strong> en expertise comptable</span>
                      </li>
                      <li className="flex items-start">
                        <CheckCircle className="w-4 h-4 text-primary-blue mr-2 mt-0.5 flex-shrink-0" />
                        <span>Cabinets prestigieux : <strong>Orial, Forvis Mazars</strong></span>
                      </li>
                      <li className="flex items-start">
                        <CheckCircle className="w-4 h-4 text-primary-blue mr-2 mt-0.5 flex-shrink-0" />
                        <span>Spécialisation <strong>PME et fonds d'investissement</strong></span>
                      </li>
                    </ul>
                  </div>

                  <div>
                    <h4 className="font-semibold text-primary-blue mb-3 flex items-center">
                      <TrendingUp className="w-5 h-5 mr-2" />
                      Domaines d'Expertise
                    </h4>
                    <div className="grid grid-cols-2 gap-2">
                      {[
                        'Expertise Comptable',
                        'Audit & Conseil',
                        'Fiscalité PME',
                        'Fonds Investissement',
                        'Immobilier',
                        'Stratégie Financière'
                      ].map((expertise) => (
                        <div key={expertise} className="bg-white p-2 rounded border border-accent-blue text-sm text-center">
                          {expertise}
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="pt-4 border-t border-gray-200">
                    <p className="text-sm">
                      <strong>Outils maîtrisés :</strong> Cegid Expert, ACD-Cador, Loop, Pennylane
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          <div className="text-center mt-12">
            <p className="text-lg text-text-gray mb-6">
              Une expertise complète en comptabilité, fiscalité et conseil pour un accompagnement personnalisé de votre entreprise
            </p>
            <Button asChild size="lg" className="btn-primary px-8 py-4 text-lg">
              <Link to="/contact">
                Rencontrer l'équipe
                <ArrowRight className="w-5 h-5 ml-2" />
              </Link>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;
